import React, {  forwardRef,  useImperativeHandle, useRef, useState } from  "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCIcon, RCCInput,  RCCRefModalProps, RCCServicesApi,  RCCTooltip } from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import DateTimePicker from "src/components/DateTimePicker";
import DeleteModal from "src/components/DeleteModal";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import SortableHandle from "src/components/Sortable/Handle";
import Tabs from "src/components/Tabs";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import LovDiagnosticModels from "src/components/Lov/DiagnosticModels";
import Audit from "src/components/Audit";
import { v4 } from "uuid";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { IComponentDiagnosticModals, IMdtbb001, IRefDiagnosticModals } from "./interface";
import { addAndChangeSchema, deleteSchema } from "./schemas";

const Modals: React.ForwardRefRenderFunction<IRefDiagnosticModals, IComponentDiagnosticModals> = ({
	callbackAddOrChange,
	callbackDelete
}, ref) => {

	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const {addToast} = RCCHookToast();

	const modalDelete = useRef<RCCRefModalProps>(null);

	const { 
		handleSubmit, 
		register, 
		control, 
		reset, 
		setError, 
		formState: { errors }, 
		clearErrors
	} = useForm();

	const { 
		handleSubmit : handleSubmitDelete, 
		register : registerDelete, 
		reset : resetDelete, 
		setError : setErrorDelete, 
		formState: { errors : errorsDelete }, 
		clearErrors: clearErrorsDelete 
	} = useForm();

	const {
		fields: mdtbb004,
		append,
		remove,
		move
	} = useFieldArray({
		name:"mdtbb004",
		control
	});

	const [isAdd, setIsAdd] = useState(false);
	const [selectedDiagnostic, setSelectedDiagnostic] = useState<IMdtbb001>({} as IMdtbb001);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);

	const queryData = async (id: number) => {
		setLoading(true);
		try{
			const { data } = await RCCServicesApi.getRequest({
				method: "Diagnostics/Select",
				urlParameters : {
					id
				}
			});
			setSelectedDiagnostic(data);
			append(data.mdtbb004);
			setLoading(false);
			modalAddOrChange.current?.openModal();
		}catch(err: any){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};

	const submitAddOrChange = async (data: any) => {
		try{
			clearErrors();
			modalAddOrChange.current?.setLoadingButton();

			const dataChanged = data;
			dataChanged.mdb001Mda001Id = Number(data.mdb001Mda001Id) ? parseInt(data.mdb001Mda001Id,10) : null;

			await addAndChangeSchema(translate, isAdd).validate({...data}, {
				abortEarly: false
			});

			let idForCallback: any = !isAdd ? selectedDiagnostic.mdb001Id : 0;

			if(isAdd){
				const response = await RCCServicesApi.postRequest({
					method: "Diagnostics/Insert",
					body: {
						...data
					}
				});
				idForCallback = response.data;
			}else{
				await RCCServicesApi.putRequest({
					method: "Diagnostics/Update",
					body: {
						...data,
						mdb001Mda001Id: selectedDiagnostic.mdb001Mda001Id,
						mdb001Id: selectedDiagnostic.mdb001Id
					}
				});
			}

			if(Number(idForCallback))
				callbackAddOrChange(idForCallback);

			addToast({
				color: "green",
				description: isAdd ? translate("Diagnóstico adicionado com sucesso") : translate("Diagnóstico alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();

		}catch(err: any){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar diagnóstico") : translate("Erro ao alterar diagnóstico"), addToast);
		}
	};

	const submitDelete = async (data: any) => {
		try{	
			clearErrorsDelete();
			modalDelete.current?.setLoadingButton();

			await deleteSchema(translate).validate({...data, "mdb001DescricaoConfirmation": selectedDiagnostic.mdb001Descricao},{
				abortEarly: false
			});

			await RCCServicesApi.deleteRequest({
				method: "Diagnostics/Delete",
				urlParameters: {
					id: selectedDiagnostic.mdb001Id
				}
			});

			callbackDelete(selectedDiagnostic.mdb001Id);

			addToast({
				color: "green",
				description: translate("Diagnóstico excluído com sucesso"),
				title: translate("Sucesso")
			});
			modalDelete.current?.resetLoadingButton();
			modalDelete.current?.closeModal();


		}catch(err: any){

			modalDelete.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorDelete))
				return;
			standardErrorMessages(err, translate("Erro ao excluir diagnóstico"), addToast);
		}	
	};

	const handleDragEnd = (activeId: string, overId: string) => {
		const activeIndex = mdtbb004.findIndex((checks) => checks.id === activeId);
		const overIndex = mdtbb004.findIndex((checks) => checks.id === overId);
		move(activeIndex, overIndex);
	};
	
	const actionButtonAdd = () => {
		reset();
		setIsAdd(true);
		setSelectedDiagnostic({} as IMdtbb001);
		modalAddOrChange.current?.openModal();
	};

	const actionButtonChange = (id: number) => {
		reset();
		setIsAdd(false);
		queryData(id);
	};

	const actionButtonDelete = (id: number, title: string) => {
		resetDelete(); 
		setSelectedDiagnostic({
			mdb001Id: id,
			mdb001Descricao: title
		} as IMdtbb001); 
		modalDelete.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));
    

	return (
		<>
			<DeleteModal
				ref={modalDelete}
				title= {translate("Exclusão de diagnóstico")}
				description= {`${translate("Deseja realmente excluir o diagnóstico")}: ${selectedDiagnostic.mdb001Descricao}?`}
				onSubmit = {handleSubmitDelete(submitDelete)} 
				height="250px"
			>
				<RCCGrid
					container
					item
					xs={10}
				>
					<RCCInput 
						register={registerDelete}
						name="mdb001Descricao"
						label={translate("Digite o nome do diagnóstico para excluí-lo")}
						maxLength={200}
						error={errorsDelete.mdb001Descricao}
						required
					/>
				</RCCGrid>
			</DeleteModal>

			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Adicionar Diagnóstico") : translate("Editar Diagnóstico"),
					subtitle: !isAdd ? `${translate("Diagnóstico")}: ${selectedDiagnostic.mdb001Descricao}`: "",
					icon: {
						name: isAdd ? "plus" : "pencil"
					}
				}}
				onSubmit={handleSubmit(submitAddOrChange)}
				styleInline={ModalSide}
			>

				<Tabs 
					name="tabsDiagnostics"
					tabInit={0}
					options={[
						{
							label: translate("Geral"),
							content: (
								<>
									<RCCGrid
										container
										item
										direction="column"
										alignItems="center"
									>
										<RCCGrid
											container
											item
											xs={12}
											sm={10}
											alignItems="center"
											justify="flex-end"
										>
											<Audit data={selectedDiagnostic} type="pop"/>
										</RCCGrid>
										{isAdd && (
											<RCCGrid
												container
												item
												xs={12}
												sm={10}
											>
												<LovDiagnosticModels 
													control={control}
													name="mdb001Mda001Id"
													label={translate("Modelo de diagnóstico de negócio")}
													error={errors.mdb001Mda001Id}
													required
												/>
											</RCCGrid>
										)}
										<RCCGrid
											container
											item
											xs={12}
											sm={10}
										>
											<RCCInput 
												register={register}
												name="mdb001Descricao"
												label={translate("Descrição")}
												maxLength={60}
												defaultValue={selectedDiagnostic.mdb001Descricao ?? ""}
												error={errors.mdb001Descricao}
												required
											/>
										</RCCGrid>

										<RCCGrid
											container
											item
											xs={12}
											sm={10}
										>
											<DateTimePicker 
												type="datePicker"
												control={control}
												name="mdb001Data"
												label={translate("Data")}
												defaultValue={selectedDiagnostic.mdb001Data ?? new Date()}
												error={errors.mdb001Data}
												required
											/>
										</RCCGrid>
									</RCCGrid>
								</>
							)
						},
						{
							label: translate("Comparativos"),
							content: (
								<RCCGrid container justifyContent="center">
									<RCCGrid item sm={10}>
										<RCCGrid container justifyContent="space-between" alignContent="flex-end" wrap="nowrap">
											{errors.options &&
												<RCCGrid container item alignItems="center">	
													<RCCIcon name="errorOutline" size={19} color="var(--red)"/>
												</RCCGrid>
											}	
											<RCCGrid container item justifyContent="flex-end">
												<RCCTooltip
													text={translate("Novo Comparativo")}
													color="purple"
												>
													<RCCButton
														icon={{name: "plus"}}
														color="purple"
														size="sm"
														layout="isRounded"
														onClick={() => {
															clearErrors("options");
															append({
																id: v4()
															});
														}}
													/>
												</RCCTooltip>
											</RCCGrid>
										</RCCGrid>

										{mdtbb004.length === 0 && (
											<RCCAlert
												color="grey"
												title={translate("Nenhum comparativo adicionado")}
												description={translate("Aperte no botão acima para adicionar")}
											/>
										)}

										<RCCGrid item>

											<SortableContainer 
												items={mdtbb004.map((comparative) => ({
													...comparative,
													id: `${comparative.id.toString()}`
												}))}
												onDragEnd={handleDragEnd}
												modifiers={[restrictToVerticalAxis]}
											>	
												{mdtbb004.map((comparative: any,index) => (
													<SortableItem id={comparative.id} key={comparative.id}>
														<RCCGrid 
															key={comparative.id} 
															item container 
															alignItems="baseline"
															justifyContent="space-between"
														>
															<RCCGrid item xs={10}>
																<RCCInput 
																	register={register}
																	name={`mdtbb004.${index}.mdb004Descricao`}
																	label={translate("Descrição")}
																	error={errors.mdtbb004 && errors.mdtbb004[index]?.mdb004Descricao}
																	defaultValue={comparative.mdb004Descricao}
																	maxLength={40}
																	required
																/>
															</RCCGrid>
															<SortableHandle>
																<RCCTooltip
																	text={translate("Mover Comparativo")}
																	color="grey"
																>
																	<RCCButton
																		color="transparent"
																		icon={{
																			name: "arrows",
																			color: "var(--grey)"
																		}}
																		layout="isRounded"
																		// disabled={disabled}
																		tabIndex={-1}
																	/>
																</RCCTooltip>
															</SortableHandle>
															<RCCTooltip
																text={translate("Excluir Comparativo")}
																color="red"
															>
																<RCCButton
																	icon={{name: "trash"}}
																	color="red"
																	size="sm"
																	layout="isRounded"
																	onClick={() => remove(index)}
																/>
															</RCCTooltip>
								
															<hr/>
														</RCCGrid>
													</SortableItem>
												))}
											</SortableContainer>
										</RCCGrid>
									</RCCGrid>
								</RCCGrid>
							)
						}
					]}
				/>



			
			</Modal>
		</>
	);
};


export default forwardRef(Modals);
